import { useState, useEffect, useMemo, Suspense } from 'react';
import { FormControl, makeStyles, InputLabel, Select, MenuItem, Typography } from '@material-ui/core';
import { commonStrings, homeStrings } from '../../../../components/StringBundle';
import Loading from '../../../../components/Loading';
import Snackbar from '../../../../components/Snackbar';
import useToken from '../../../../hooks/useToken';
import { getMyPowerBiReports } from '../../../../apis';
import { DASHBOARD_COMPANY_STORAGE_KEY, POWER_BI_LOCKED_COMPANY } from '../../../../constants/powerBi';
import { company_name_list } from '../../../common';
import { decryptEmbedUrl } from '../../PowerBI/Service';
import { inAppDashboards } from './constant';

const menuProps = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  getContentAnchorEl: null,
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    height: '100%',
    width: '100%',
    marginTop: theme.spacing(1),
  },
  filterRow: {
    flexShrink: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  formControl: {
    margin: 0,
    minWidth: 240,
    maxWidth: 320,
  },
  companyControl: {
    margin: 0,
    minWidth: 140,
  },
  customWrap: {
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'auto',
    width: '100%',
  },
  iframeWrap: {
    flex: '1 1 auto',
    minHeight: 0,
    position: 'relative',
    width: '100%',
  },
  iframeFull: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block',
  },
  comingSoon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#888',
  },
  emptyWrapper: {
    textAlign: 'center',
    marginTop: '5em',
    width: '100%',
  },
}));

const Home = () => {
  const classes = useStyles();
  const { getCurrentUserDetails } = useToken();
  const currentUserRole = getCurrentUserDetails()?.role;

  // Each option is a Power BI report the user can see (url) or an in-app dashboard for their role (component).
  const [company, setCompany] = useState(
    POWER_BI_LOCKED_COMPANY ?? (sessionStorage.getItem(DASHBOARD_COMPANY_STORAGE_KEY) || 'All')
  );
  const [dashboardOptions, setDashboardOptions] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: '' });

  // Built once per role in a single pass, not again on every company change.
  const inApp = useMemo(
    () =>
      inAppDashboards.reduce((options, { id, label, component, roles }) => {
        if (roles.has(currentUserRole)) options.push({ id, label, component });
        return options;
      }, []),
    [currentUserRole]
  );

  useEffect(() => {
    setLoading(true);
    setError(null);
    getMyPowerBiReports(company)
      .then((data) => {
        const reports = (data?.reports || []).map((report) => ({
          id: report.pbi_report_id,
          label: report.report_name,
          url: decryptEmbedUrl(report.embed_url),
        }));
        const all = [...reports, ...inApp];
        setDashboardOptions(all);
        setSelectedId(all[0]?.id || '');
      })
      .catch(() => {
        // In-app dashboards don't depend on the Power BI call, so still offer them.
        setDashboardOptions(inApp);
        setSelectedId(inApp[0]?.id || '');
        // With nothing to show, the page itself says so; otherwise a snackbar flags the missing Power BI reports.
        if (inApp.length === 0) setError(homeStrings.reportsLoadFailed);
        else setSnack({ open: true, message: homeStrings.reportsLoadFailed, severity: 'error' });
      })
      .finally(() => setLoading(false));
  }, [company, inApp]);

  function handleCompanyChange(e) {
    setCompany(e.target.value);
    sessionStorage.setItem(DASHBOARD_COMPANY_STORAGE_KEY, e.target.value);
  }

  const selected = dashboardOptions.find((option) => option.id === selectedId);
  const ActiveComponent = selected?.component;

  function renderContent() {
    if (loading) return <Loading />;
    if (error) {
      return (
        <div className={classes.emptyWrapper}>
          <Typography variant="h6" color="error">{error}</Typography>
        </div>
      );
    }
    if (dashboardOptions.length === 0) {
      return (
        <div className={classes.emptyWrapper}>
          <Typography variant="h6">{homeStrings.noReportsAvailable}</Typography>
        </div>
      );
    }
    if (ActiveComponent) {
      return (
        <div className={classes.customWrap}>
          <Suspense fallback={<Loading />}>
            <ActiveComponent />
          </Suspense>
        </div>
      );
    }
    return (
      /* Dashboard iframe — fills all remaining height */
      <div className={classes.iframeWrap}>
        {selected?.url ? (
          <iframe title="powerbi-report" className={classes.iframeFull} src={selected.url} />
        ) : (
          <div className={classes.comingSoon}>
            <h2>{homeStrings.comingSoon}</h2>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {snack.open && (
        <Snackbar {...snack} handleClose={() => setSnack({ open: false, message: '', severity: '' })} />
      )}
      {/* Filter row stays visible while loading or empty, so the company can always be changed back */}
      <div className={classes.filterRow}>
        {/* Hidden while Power BI is locked to one company (POWER_BI_LOCKED_COMPANY). */}
        {!POWER_BI_LOCKED_COMPANY && (
          <FormControl variant="outlined" className={classes.companyControl} size="small">
            <InputLabel id="dashboard-company">{commonStrings.company}</InputLabel>
            <Select
              labelId="dashboard-company"
              value={company}
              onChange={handleCompanyChange}
              label={commonStrings.company}
              id="dashboard-company-select"
              MenuProps={menuProps}
            >
              {company_name_list.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!loading && !error && dashboardOptions.length > 0 && (
          <FormControl variant="outlined" className={classes.formControl} size="small">
            <InputLabel id="report">{homeStrings.reports}</InputLabel>
            <Select
              labelId="report"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              label={homeStrings.reports}
              id="reports-select"
              MenuProps={menuProps}
            >
              {dashboardOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>

      {renderContent()}
    </div>
  );
};

export default Home;
